/**
 * The docket as a decision queue — `design_handoff_docket`.
 *
 * WHAT THIS REPLACES, because the deletions are the design and each was a structural defect
 * rather than a styling one:
 *
 *   THE SAME PROPOSAL RENDERED UP TO THREE TIMES IN FULL. Once as a draft under "Your proposals",
 *   again there as `On the docket`, and a third time under "The 2026 docket" — every section and
 *   every explanation bullet, three times on one page, with a `Mine` / `Docket` toggle that
 *   filtered nothing. A saved draft and a docketed proposal are two records of one intent; the
 *   draft is now ONE LINE inside the docketed row, with one action on it.
 *
 *   THE THRESHOLD RESTATED ON EVERY CARD. "A re-vote needs the full super-majority of 7…"
 *   verbatim twice and a variant on the third. The threshold is a property of the NIGHT, not of
 *   each item: everybody turned up, so the relaxed six cannot apply to anything on the docket and
 *   all three items need seven. Said once, that is twenty words; restated per card it is sixty
 *   words of near-identical text a reader learns to skip — which buries the one fact that
 *   actually changed tonight.
 *
 *   FULL STATUTORY TEXT ALWAYS EXPANDED. Four thousand words of scroll. The rows carry a
 *   one-line gist and open one at a time.
 *
 * THE GIST IS NOT A SUMMARY OF THE TEXT IN PLACE OF IT. Proposal copy is authored by GMs and is
 * verbatim, always: the gist sits BESIDE it, is written by somebody, and is hidden the moment
 * the row is expanded — an item with its own text on screen does not need a paraphrase above it.
 * Where nobody has written one there is no gist, and nothing is invented to fill the space.
 */

import {
  RELAXED_MAJORITY, SUPER_MAJORITY, QUORUM, tallyFrom, tallyOutcome, type VoteTally,
} from './proposals.js';
import type { ProposalOutcome, Vote } from './proposals.js';

/* -- the rows ---------------------------------------------------------------------------------- */

export type RowState =
  'up-next' | 'to-come' | 'on-floor' | 'passed' | 'failed' | 'provisional' | 'no-quorum';

export interface DocketRow {
  /** Running order, 1-based. */
  n: number;
  id: string;
  title: string;
  gm: string;
  /** The viewer's own proposal — a mark on the row, never a second list. */
  mine: boolean;
  /** A conditional pass in a prior year, coming back for the vote that makes it permanent. */
  revote: boolean;
  /** One authored sentence, or empty. Never derived from the body. */
  gist: string;
  state: RowState;
  /** What the chip says: `up next`, `on the floor`, `passed 8–2`. */
  label: string;
  /** An unsubmitted draft of the same intent, still saved. One line, one action. */
  staleDraftAt: string | null;
  /** Rule ids this rewrites. Rendered only when there are some. */
  touches: readonly string[];
}

export interface DocketProposal {
  id: string;
  title: string;
  gm: string;
  body?: string;
  amends?: readonly string[];
  isRevote?: boolean;
  gist?: string;
  status?: string;
  recordedAt?: string;
  outcome?: ProposalOutcome;
  votes?: Record<string, Vote>;
}

export interface DocketInput {
  /** In the commissioners' order. */
  agenda: readonly DocketProposal[];
  openItemId?: string;
  closedIds?: readonly string[];
  me: string;
  /** Gists written elsewhere, keyed by proposal id. `p.gist` wins over this. */
  gists?: Readonly<Record<string, string>>;
  /** Drafts the viewer still has saved, keyed by the docketed proposal they duplicate. */
  staleDrafts?: Readonly<Record<string, string>>;
  /** How many seats are represented tonight, for the outcome readout. */
  represented?: number;
  absent?: number;
}

const up = (s: string | null | undefined) => (s ?? '').toUpperCase();

export function docketRows(i: DocketInput): DocketRow[] {
  const closed = new Set(i.closedIds ?? []);
  const me = up(i.me);
  /* The first item nobody has heard yet — the only one that can be `up next`. */
  const nextId = i.openItemId ? null
    : i.agenda.find((p) => !closed.has(p.id) && !p.recordedAt)?.id ?? null;

  return i.agenda.map((p, idx) => {
    const decided = decidedState(p, i);
    const state: RowState = decided ?? (p.id === i.openItemId ? 'on-floor'
      : p.id === nextId ? 'up-next' : 'to-come');

    return {
      n: idx + 1,
      id: p.id,
      title: p.title,
      gm: p.gm,
      mine: up(p.gm) === me,
      revote: Boolean(p.isRevote),
      gist: (p.gist ?? i.gists?.[p.id] ?? '').trim(),
      state,
      label: labelFor(state, p, i),
      staleDraftAt: i.staleDrafts?.[p.id] ?? null,
      touches: p.amends ?? [],
    };
  });
}

/** Only a recorded item has an outcome. An open one with votes on it is still being decided. */
function decidedState(p: DocketProposal, i: DocketInput): RowState | null {
  if (!p.recordedAt) return null;
  const o = outcomeOf(p, i);
  return o === 'passed' ? 'passed'
    : o === 'provisional' ? 'provisional'
      : o === 'no-quorum' ? 'no-quorum' : 'failed';
}

/**
 * WHAT THE ROOM DECIDED, and the stored answer beats the recomputed one.
 *
 * `outcome` and `status` are what a commissioner actually wrote down; re-deriving from the votes
 * would quietly overrule him — and it would do it with tonight's attendance rather than the
 * attendance the item was heard under, which is a different question a year later.
 */
function outcomeOf(p: DocketProposal, i: DocketInput): ProposalOutcome {
  if (p.outcome) return p.outcome;
  if (p.status === 'passed' || p.status === 'provisional' || p.status === 'failed') {
    return p.status as ProposalOutcome;
  }
  return tallyOutcome(tally(p, i), { isRevote: p.isRevote }).outcome;
}

const tally = (p: DocketProposal, i: DocketInput): VoteTally =>
  tallyFrom(p.votes ?? {}, i.represented ?? Object.keys(p.votes ?? {}).length);

function labelFor(state: RowState, p: DocketProposal, i: DocketInput): string {
  if (state === 'up-next') return 'up next';
  if (state === 'to-come') return 'to come';
  if (state === 'on-floor') return 'on the floor';
  if (state === 'no-quorum') return 'no quorum';
  const t = tally(p, i);
  /*
   * THE MARGIN IS AGAINST THE ROOM, NOT AGAINST THE YES COUNT. `8–2` means eight of the ten
   * seats said yes; an abstention is a no by 2015-I-1, so it belongs on the right of the dash
   * rather than nowhere.
   */
  const against = t.no + t.abstain;
  const margin = t.yes + against > 0 ? ` ${t.yes}–${against}` : '';
  if (state === 'provisional') return `passed${margin} · one season`;
  return `${state === 'passed' ? 'passed' : 'failed'}${margin}`;
}

/* -- the threshold, said once for the night ----------------------------------------------------- */

export interface NightThreshold {
  /** The sentence, in ink. */
  said: string;
  /** The qualifier under it. */
  why: string;
  /** False when one number does not cover the whole docket — then `why` carries the exception. */
  uniform: boolean;
}

export interface ThresholdInput {
  represented: number;
  seats: number;
  /** How many items on the docket are re-votes. */
  revotes: number;
}

/**
 * WHAT TONIGHT NEEDS, once.
 *
 * Three rules interact and the interaction is the whole point: 2015-I-1 gives a relaxed six when
 * somebody is unrepresented, 2019-I-2 §2 withholds it from a re-vote whatever the attendance, and
 * quorum is eight. Nobody in a room at nine in the evening reconstructs that from three citations,
 * and a per-card restatement of it is the same twenty words three times.
 */
export function nightThreshold(i: ThresholdInput): NightThreshold {
  if (i.represented < QUORUM) {
    return {
      said: `Nothing can be decided tonight.`,
      why: `Quorum is ${QUORUM} and ${i.represented} of ${i.seats} are represented, so no vote `
        + 'on this docket counts.',
      uniform: true,
    };
  }
  if (i.represented >= i.seats) {
    return {
      said: `Every item tonight needs ${SUPER_MAJORITY}.`,
      why: `All ${i.seats} are here, and the relaxed ${RELAXED_MAJORITY} only applies when `
        + 'somebody is unrepresented.',
      uniform: true,
    };
  }
  const base = `${SUPER_MAJORITY} makes an item permanent tonight; ${RELAXED_MAJORITY} passes it `
    + 'for one season.';
  if (i.revotes === 0) {
    return {
      said: base,
      why: `${i.represented} of ${i.seats} are represented, so the relaxed ${RELAXED_MAJORITY} is `
        + 'available.',
      uniform: true,
    };
  }
  return {
    said: base,
    why: `${i.represented} of ${i.seats} are represented, so the relaxed ${RELAXED_MAJORITY} is `
      + `available — except on the ${word(i.revotes)} re-vote${i.revotes === 1 ? '' : 's'}, which `
      + `${i.revotes === 1 ? 'needs' : 'need'} ${SUPER_MAJORITY} either way.`,
    uniform: false,
  };
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const word = (n: number) => WORDS[n] ?? String(n);

/* -- what the card is called, and what it says at the foot -------------------------------------- */

export type DocketPhase = 'docket' | 'meeting' | 'committed';

export interface DocketHead {
  title: string;
  sub: string;
  count: string;
}

export function docketHead(phase: DocketPhase, items: number): DocketHead {
  return {
    title: phase === 'committed' ? 'What the room decided' : 'The docket',
    sub: phase === 'committed'
      ? 'in the order they were heard'
      : 'in the order they will be heard',
    count: `${items} item${items === 1 ? '' : 's'}`,
  };
}

export interface FootInput {
  phase: DocketPhase;
  /** When submissions closed. */
  closedAt?: string | null;
  /** When the record was committed. */
  committedAt?: string | null;
  /** How the deadline reads on the face — the caller owns the clock. */
  closedLabel?: string;
  committedLabel?: string;
}

export function docketFoot(i: FootInput): string {
  if (i.phase === 'committed' && i.committedAt) {
    return `Committed to the rulebook${i.committedLabel ? ` at ${i.committedLabel}` : ''}. `
      + 'Every vote is recorded against the rule it changed.';
  }
  if (i.closedAt) {
    return `Submissions closed${i.closedLabel ? ` on ${i.closedLabel}` : ''}. `
      + 'Nothing can be added tonight.';
  }
  return 'Submissions are still open.';
}

/* -- the summary line, once it is over ---------------------------------------------------------- */

/** "Two passed, one failed" — the adjourned strip's right-hand fact. */
export function decidedSummary(rows: readonly DocketRow[]): string {
  const passed = rows.filter((r) => r.state === 'passed' || r.state === 'provisional').length;
  const failed = rows.filter((r) => r.state === 'failed' || r.state === 'no-quorum').length;
  const parts = [
    passed ? `${cap(word(passed))} passed` : '',
    failed ? `${passed ? word(failed) : cap(word(failed))} failed` : '',
  ].filter(Boolean);
  return parts.join(', ');
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* -- the draft that is really the same proposal -------------------------------------------------- */

/**
 * A saved draft that duplicates something already on the docket.
 *
 * MATCHED BY INTENT, NOT BY TITLE EQUALITY. A GM writes a proposal, a commissioner tidies the
 * wording before it goes on the agenda, and the two titles are then similar rather than equal —
 * which is exactly the case the live build rendered as two separate proposals, both in full.
 *
 * Conservative on purpose. Same author, and most of the shorter title's real words shared: a
 * false positive hides one man's genuinely separate proposal behind another's row, which is a
 * worse failure than a duplicate line he can ignore.
 */
export function staleDrafts(
  drafts: readonly { id: string; gm: string; title: string; updatedAt?: string }[],
  agenda: readonly DocketProposal[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const d of drafts) {
    const hit = agenda.find((p) => up(p.gm) === up(d.gm) && sameIntent(p.title, d.title));
    if (hit && !out[hit.id]) out[hit.id] = d.updatedAt ?? '';
  }
  return out;
}

const STOP = new Set(['the', 'a', 'an', 'of', 'to', 'and', 'for', 'in', 'on', 'at', 'by', 'is']);

const words = (s: string) => s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
  .filter((w) => w.length > 2 && !STOP.has(w));

export function sameIntent(a: string, b: string): boolean {
  const x = new Set(words(a));
  const y = new Set(words(b));
  if (x.size === 0 || y.size === 0) return false;
  let shared = 0;
  for (const w of y) if (x.has(w)) shared += 1;
  return shared / Math.min(x.size, y.size) >= 0.6;
}
